import {Fragment} from 'react';

import styled from 'styled-components';

import {SkeletonLoader} from '~/shared/components/Loaders';
import {getLocalizationService} from '~/shared/services/localisationService';
import {DETAILS_TYPES} from '~/shared/consts/userReportConsts';
import TenbisIcon from '~/shared/utils/payments/cardsPie/moneycardSmall.svg?react';
import {flipOnLTR} from '~/shared/theme/flipOnLTR';

import PrepaidCard from '../../PrepaidCard';

import CreditSection from './CreditSection';
import {DesktopLoaderComponent} from './LoaderComponents';
import MoneyCardDetail from './MoneyCardDetail';
import {InnerWrapper, MoneyCardDetailWrapper, Title} from './shared.styles';

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-top: 8px;

  h1 {
    margin-right: 0;
  }
`;

const Icon = styled(TenbisIcon)`
  margin-bottom: 16px;
  ${flipOnLTR`
    margin-left: 8px;
  `}
`;

const CardDeleted = styled(Title)`
  margin-top: 24px;
  color: ${({theme}) => theme.colors.error};
  ${flipOnLTR`margin-right: 8px;`}
`;

const CardDeletedLine = styled(Title)`
  ${flipOnLTR`margin-right: 8px;`}
`;

export const DesktopMoneyCardDetails = ({moneyCardsList, reportSetting, isIntelReport, setOrderListData, onAutoCreditSubscribeSuccess}) => {
  const {t} = getLocalizationService();

  return (
    <SkeletonLoader shouldShowLoader={!moneyCardsList} LoaderComponent={DesktopLoaderComponent}>
      {moneyCardsList?.map(moneyCard => {
        const {
          cardSuffix,
          limitation,
          usage,
          balance,
          usageBruto,
          usageNet,
          personalGuestTotal,
          businessMeetingGuestTotal,
          moneycardId,
          tenbisCreditConversion,
          isTenbisCredit,
          isPrepaid,
          cardDeleted,
          encryptedMoneycardId,
        } = moneyCard;

        return (
          <Fragment key={cardSuffix}>
            {isPrepaid && (
              <PrepaidCard isTenbisCredit={isTenbisCredit} balance={balance.monthly} cardSuffix={cardSuffix} />
            )}
            {!isTenbisCredit && !isPrepaid && (
              <>
                <Header>
                  <Icon />
                  <Title as="h1">{t('card_number_xxxx', {cardSuffix})}</Title>
                  {cardDeleted && (
                  <>
                    <CardDeletedLine>-</CardDeletedLine>
                    <CardDeleted>{t('cancelled_card')}</CardDeleted>
                  </>
                )}
                </Header>
                <MoneyCardDetailWrapper>
                  {!isIntelReport && (
                    <InnerWrapper>
                      <MoneyCardDetail
                        daily={!limitation.hideDailyLimit && {sum: limitation.daily, text: t('daily_limitation')}}
                        weekly={limitation.showWeeklyLimit && {sum: limitation.weekly, text: t('weekly_limitation')}}
                        monthly={
                          !limitation.hideMonthlyLimit && {
                            sum: limitation.monthly,
                            text: t('monthly_limitation'),
                          }
                        }
                        type={DETAILS_TYPES.LIMITATION}
                      />
                    </InnerWrapper>
                  )}
                  <InnerWrapper>
                    <MoneyCardDetail
                      daily={!usage.hideDailyLimit && {sum: usage.daily, text: t('used_today')}}
                      weekly={usage.showWeeklyLimit && {sum: usage.weekly, text: t('used_this_week')}}
                      monthly={
                        !usage.hideMonthlyLimit && {
                          sum: usage.monthly,
                          text: t('used_this_month'),
                        }
                      }
                      type={DETAILS_TYPES.USAGE}
                    />
                  </InnerWrapper>
                  {!isIntelReport && (
                    <InnerWrapper>
                      <MoneyCardDetail
                        daily={
                          !reportSetting.hideDailyBalanceInUserReport &&
                          !balance.hideDailyLimit && {sum: balance.daily, text: t('daily_balance')}
                        }
                        weekly={balance.showWeeklyLimit && {sum: balance.weekly, text: t('weekly_balance')}}
                        monthly={
                          !balance.hideMonthlyLimit && {
                            sum: balance.monthly,
                            text: t('monthly_balance'),
                          }
                        }
                        type={DETAILS_TYPES.BALANCE}
                      />
                    </InnerWrapper>
                  )}

                  {isIntelReport && (
                    <>
                      <InnerWrapper>
                        <MoneyCardDetail
                          daily={
                            !reportSetting.hideDailyBalanceInUserReport &&
                            !usageBruto.hideDailyLimit && {sum: usageBruto.daily, text: t('daily_bruto')}
                          }
                          weekly={usageBruto.showWeeklyLimit && {sum: usageBruto.weekly, text: t('weekly_bruto')}}
                          monthly={
                            !usageBruto.hideMonthlyLimit && {
                              sum: usageBruto.monthly,
                              text: t('monthly_bruto'),
                            }
                          }
                          type={DETAILS_TYPES.GROSS}
                        />
                      </InnerWrapper>
                      <InnerWrapper>
                        <MoneyCardDetail
                          daily={
                            !reportSetting.hideDailyBalanceInUserReport &&
                            !usageNet.hideDailyLimit && {sum: usageNet.daily, text: t('daily_nett')}
                          }
                          weekly={usageNet.showWeeklyLimit && {sum: usageNet.weekly, text: t('weekly_nett')}}
                          monthly={
                            !usageNet.hideMonthlyLimit && {
                              sum: usageNet.monthly,
                              text: t('monthly_nett'),
                            }
                          }
                          type={DETAILS_TYPES.NETT}
                        />
                      </InnerWrapper>
                      <InnerWrapper>
                        <MoneyCardDetail
                          daily={{sum: businessMeetingGuestTotal, text: t('business_guest')}}
                          monthly={{sum: personalGuestTotal, text: t('personal_guest')}}
                          type={DETAILS_TYPES.GUEST}
                        />
                      </InnerWrapper>
                    </>
                  )}
                </MoneyCardDetailWrapper>

                <CreditSection
                  availableAmount={tenbisCreditConversion?.availableAmount}
                  moneycardId={moneycardId?.toString()}
                  setOrderListData={setOrderListData}
                  onAutoCreditSubscribeSuccess={onAutoCreditSubscribeSuccess}
                  tenbisCreditConversion={tenbisCreditConversion}
                  encryptedMoneycardId={encryptedMoneycardId}
                />
              </>
            )}
          </Fragment>
        );
      })}
    </SkeletonLoader>
  );
};
